// ============================================================
// HOOKED ENGINE — HOLO FOIL             [v2 overhaul — new]
// Terror's holofoil doctrine, ported to the Catch Card. The
// landing is the game's trophy moment; it should shimmer like a
// pulled foil. Per the holofoil skill:
//
//   • SVG feTurbulence + feDisplacementMap for smoke/fractal
//     motion — CSS gradients are too geometrically smooth to
//     read as genuine turbulence.
//   • Every tier (here: every Ilk + Legend) gets a UNIQUE motion
//     signature — never a recolor of another's geometry.
//   • Soft multiply / soft-light blends at restrained opacity so
//     the fish art underneath is never buried.
//   • useId() → filter IDs unique per instance (multi-card trap).
//   • Keyframes via ensureKeyframes into <head>, not <style>.
//
// One recipe map, one place to add a foil. HoloOverlay picks the
// recipe from fish.ilk (or 'Legend' when fish.legend).
// ============================================================

import { useId, useEffect } from "react";
import { SD, GM, ensureKeyframes } from "./theme";

// Foil-local keyframes (injected once, head-mounted, id-guarded).
function ensureFoilKeyframes() {
  ensureKeyframes("hooked-foil-kf", `
    @keyframes foilSweepDiag { 0% { transform: translate(-40%,-40%) rotate(25deg);} 100% { transform: translate(40%,40%) rotate(25deg);} }
    @keyframes foilSweepVert { 0% { transform: translateY(-60%);} 100% { transform: translateY(60%);} }
    @keyframes foilConicSpin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }
    @keyframes foilPulse { 0%,100% { opacity: 0.35;} 50% { opacity: 0.7;} }
    @keyframes foilDrift { 0% { transform: translateX(-8%);} 100% { transform: translateX(8%);} }
  `);
}

// feTurbulence smoke layer — the shared engine for organic motion.
function TurbulenceSmoke({ uid, color, baseFreq = 0.014, scale = 22, seed = 2, blend = "soft-light", opacity = 0.5, dur = 7 }) {
  const fid = `turb-${uid}`;
  return (
    <svg width="100%" height="100%" viewBox="0 0 300 380" preserveAspectRatio="none"
      style={{ position: "absolute", inset: 0, mixBlendMode: blend, opacity, pointerEvents: "none" }}>
      <defs>
        <filter id={fid} x="-20%" y="-20%" width="140%" height="140%">
          <feTurbulence type="fractalNoise" baseFrequency={baseFreq} numOctaves="3" seed={seed} result="noise">
            <animate attributeName="baseFrequency" dur={`${dur}s`}
              values={`${baseFreq};${baseFreq * 1.7};${baseFreq}`} repeatCount="indefinite" />
          </feTurbulence>
          <feDisplacementMap in="SourceGraphic" in2="noise" scale={scale} xChannelSelector="R" yChannelSelector="G" />
        </filter>
      </defs>
      <rect x="0" y="0" width="300" height="380" fill={color} filter={`url(#${fid})`} opacity="0.9" />
    </svg>
  );
}

// ── Per-Ilk recipes — each a DISTINCT motion signature ───────
const RECIPES = {
  // SPACE — a slow diagonal starlight sweep + fine star noise.
  Space: ({ uid }) => (
    <>
      <TurbulenceSmoke uid={uid} color={SD.ilkSpace} baseFreq={0.03} scale={10} seed={4} blend="screen" opacity={0.28} dur={11} />
      <div style={{
        position: "absolute", inset: "-40%",
        background: `linear-gradient(115deg, transparent 38%, ${SD.starWhite}55 48%, ${SD.foam}88 50%, ${SD.starWhite}55 52%, transparent 62%)`,
        animation: "foilSweepDiag 4.5s ease-in-out infinite alternate",
        mixBlendMode: "screen", opacity: 0.6,
      }} />
    </>
  ),

  // PHANTOM — curved, non-linear violet smoke. No linear shimmer
  // geometry (the noShimmer principle) — pure turbulence drift.
  Phantom: ({ uid }) => (
    <>
      <TurbulenceSmoke uid={uid} color={SD.ilkPhantom} baseFreq={0.011} scale={30} seed={7} blend="soft-light" opacity={0.6} dur={6} />
      <TurbulenceSmoke uid={uid + "b"} color={SD.purple} baseFreq={0.02} scale={18} seed={11} blend="screen" opacity={0.3} dur={9} />
      <div style={{
        position: "absolute", inset: 0,
        background: `radial-gradient(60% 80% at 50% 40%, ${SD.purpleLight}22, transparent 70%)`,
        animation: "foilDrift 6s ease-in-out infinite alternate", mixBlendMode: "screen",
      }} />
    </>
  ),

  // DRAGON — heat-shimmer: turbulent ember glow rising, warm bands.
  Dragon: ({ uid }) => (
    <>
      <TurbulenceSmoke uid={uid} color={SD.ilkDragon} baseFreq={0.016} scale={24} seed={3} blend="soft-light" opacity={0.5} dur={5} />
      <div style={{
        position: "absolute", inset: "-30%",
        background: `linear-gradient(180deg, transparent 30%, ${GM.goldBright}44 50%, transparent 70%)`,
        animation: "foilSweepVert 3.2s ease-in-out infinite alternate",
        mixBlendMode: "screen", opacity: 0.55,
      }} />
    </>
  ),

  // TIME — a rotating conic clock-sheen (only tier that spins).
  Time: ({ uid }) => (
    <>
      <TurbulenceSmoke uid={uid} color={SD.ilkTime} baseFreq={0.022} scale={12} seed={9} blend="screen" opacity={0.26} dur={13} />
      <div style={{
        position: "absolute", inset: "-30%", borderRadius: "50%",
        background: `conic-gradient(from 0deg, transparent, ${SD.tealLight}44 12%, transparent 24%, ${SD.caustic}33 50%, transparent 62%, ${SD.tealLight}44 84%, transparent)`,
        animation: "foilConicSpin 9s linear infinite",
        mixBlendMode: "screen", opacity: 0.5,
      }} />
    </>
  ),

  // LEGEND (the Prophecy) — everything, restrained: pale-gold
  // turbulence, slow diagonal, and a breathing halo. Rarest.
  Legend: ({ uid }) => (
    <>
      <TurbulenceSmoke uid={uid} color={SD.moonHalo} baseFreq={0.009} scale={34} seed={5} blend="soft-light" opacity={0.55} dur={8} />
      <TurbulenceSmoke uid={uid + "b"} color={GM.goldPale} baseFreq={0.018} scale={16} seed={13} blend="screen" opacity={0.35} dur={12} />
      <div style={{
        position: "absolute", inset: "-40%",
        background: `linear-gradient(125deg, transparent 40%, ${GM.goldPale}66 49%, #ffffffaa 50%, ${GM.goldPale}66 51%, transparent 60%)`,
        animation: "foilSweepDiag 6s ease-in-out infinite alternate",
        mixBlendMode: "screen", opacity: 0.7,
      }} />
    </>
  ),
};

export function HoloOverlay({ fish, radius = 14 }) {
  const uid = useId().replace(/:/g, "");
  useEffect(() => { ensureFoilKeyframes(); }, []);
  if (!fish) return null;
  const key = fish.legend ? "Legend" : (fish.ilk in RECIPES ? fish.ilk : "Space");
  const Recipe = RECIPES[key];

  return (
    <div style={{
      position: "absolute", inset: 0, borderRadius: radius,
      overflow: "hidden", pointerEvents: "none", zIndex: 1,
    }}>
      <Recipe uid={uid} />
    </div>
  );
}

export const FOIL_KEYS = Object.keys(RECIPES);
